import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { supabase } from '../lib/supabase';

interface CTAProps {
  type: 'ai_training' | 'agent_dev' | 'fractional_pm' | 'random';
  onContactClick?: () => void;
  onCalendarClick?: () => void;
  className?: string;
  blogPost?: any;
  course?: any;
}

interface CTA {
  id: string;
  type: 'ai_training' | 'agent_dev' | 'fractional_pm';
  header: string;
  description: string;
  button_text: string;
  icon: string;
  modal_type: 'calendar' | 'contact' | null;
  is_active: boolean;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

export default function DynamicCTA({ 
  type, 
  onContactClick, 
  onCalendarClick, 
  className = '',
  blogPost,
  course
}: CTAProps) {
  const [cta, setCta] = useState<CTA | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const fetchCTA = async () => {
      try {
        setLoading(true);
        setError(null);

        // Determine which type to use
        let ctaType = type;
        if (blogPost?.cta_type) {
          ctaType = blogPost.cta_type;
        } else if (course?.cta_type) {
          ctaType = course.cta_type;
        }

        // Build the query
        let query = supabase
          .from('ctas')
          .select('*')
          .eq('is_active', true);

        // Apply type filter if not random
        if (ctaType !== 'random') {
          query = query.eq('type', ctaType);
        }

        const { data, error: fetchError } = await query;

        if (fetchError) {
          throw fetchError;
        }

        if (!data || data.length === 0) {
          throw new Error('No active CTAs found');
        }

        // Select a random CTA from the results
        const randomIndex = Math.floor(Math.random() * data.length);
        setCta(data[randomIndex]);
      } catch (err) {
        console.error('Error fetching CTA:', err);
        setError(err instanceof Error ? err.message : 'Failed to load CTA');
        
        // Implement retry logic
        if (retryCount < MAX_RETRIES) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, RETRY_DELAY);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCTA();
  }, [type, retryCount, blogPost, course]);

  if (loading) {
    return <div className="animate-pulse bg-gray-200 h-48 rounded-lg"></div>;
  }

  if (error || !cta) {
    return null;
  }

  // Get the icon component dynamically
  const IconComponent = Icons[cta.icon as keyof typeof Icons] || Icons.ExternalLink;

  // Handle button click based on modal_type
  const handleClick = () => {
    if (cta.modal_type === 'calendar' && onCalendarClick) {
      onCalendarClick();
    } else if (cta.modal_type === 'contact' && onContactClick) {
      onContactClick();
    }
  };

  // Get the appropriate link based on CTA type
  const getLink = () => {
    switch (cta.type) {
      case 'ai_training':
        return '/courses';
      case 'agent_dev':
        return '/contact';
      case 'fractional_pm':
        return '/fractional-pm';
      default:
        return '/';
    }
  };

  return (
    <div className={`bg-gray-50 rounded-lg p-8 border border-gray-200 shadow-sm ${className}`}>
      <div className="text-center max-w-3xl mx-auto">
        <h3 className="text-2xl font-semibold text-gray-900 mb-4">
          {cta.header}
        </h3>
        <p className="text-gray-600 mb-6">
          {cta.description}
        </p>
        {cta.modal_type ? (
          <button
            onClick={handleClick}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-black hover:bg-gray-800 transition-colors duration-200"
          >
            <IconComponent className="mr-2 h-5 w-5" />
            {cta.button_text}
          </button>
        ) : (
          <Link
            to={getLink()}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-black hover:bg-gray-800 transition-colors duration-200"
          >
            <IconComponent className="mr-2 h-5 w-5" />
            {cta.button_text}
          </Link>
        )}
      </div>
    </div>
  );
}